// ============================================================
// useKeyboardControls — Arrow keys, space, shift+arrow, F key
// Attaches keyboard listeners for the replay page
// ============================================================

import { useEffect } from "react"

interface KeyboardControlsOptions {
  onStepForward: () => void
  onStepBackward: () => void
  onJumpNextSegment: () => void
  onJumpPreviousSegment: () => void
  onToggleAutoPlay: () => void
  onFlipBoard: () => void
  onStart?: () => void
  enabled: boolean
}

export function useKeyboardControls({
  onStepForward,
  onStepBackward,
  onJumpNextSegment,
  onJumpPreviousSegment,
  onToggleAutoPlay,
  onFlipBoard,
  onStart,
  enabled,
}: KeyboardControlsOptions) {
  useEffect(() => {
    if (!enabled) return

    function handleKeyDown(e: KeyboardEvent) {
      // Don't capture keys when user is typing in an input
      const target = e.target as HTMLElement
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return
      }

      switch (e.key) {
        case "ArrowRight":
          e.preventDefault()
          if (e.shiftKey) {
            onJumpNextSegment()
          } else {
            onStepForward()
          }
          break

        case "ArrowLeft":
          e.preventDefault()
          if (e.shiftKey) {
            onJumpPreviousSegment()
          } else {
            onStepBackward()
          }
          break

        case " ":
          e.preventDefault()
          onToggleAutoPlay()
          break

        case "f":
        case "F":
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault()
            onFlipBoard()
          }
          break

        case "Enter":
          if (onStart) {
            e.preventDefault()
            onStart()
          }
          break
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [
    enabled,
    onStepForward,
    onStepBackward,
    onJumpNextSegment,
    onJumpPreviousSegment,
    onToggleAutoPlay,
    onFlipBoard,
    onStart,
  ])
}
